// import React from 'react';
import { createSelector } from "reselect";
// import ls from "local-storage";
import * as ls from "local-storage";
import config, { GameStateType, StateType } from "../constants/config";
import {
  formatEq,
  getTotal,
  hasAchieved,
  // getOldGridList,
  // getGridData,
} from "../utils/gameHelpers";
import {
  AchievementType,
  HistoryType,
  MessageType,
} from "../types/generalTypes";

// Helper: detect if we should force mobile layout (for app preview video)
function isForceMobile() {
  return (
    typeof window !== "undefined" &&
    window.location.search.includes("forceScale=3")
  );
}

const getApp = (state: StateType) => state.app;
const getGame = (state: StateType) => state.game;

export const getEquation = createSelector([getGame], (game) =>
  (game.eq || []).slice(0, game.eqPos)
);
export const getFormattedEquation = createSelector([getEquation], (equation) =>
  formatEq(equation)
);
export const getEquationTotal = createSelector([getEquation], (equation) =>
  getTotal(equation)
);
export const getIsComplete = createSelector(
  [getEquationTotal, getGame],
  (total, game) => game.target === total && total > 0
);
export const getSolutions = createSelector(
  [getApp, getGame],
  (app, game) => game.solutions // TODO: local storage
);
export const getShowReset = createSelector(
  getGame,
  (game) => (game.eqPos || 0) > 0
);
export const getShowUndo = createSelector(
  [getGame],
  (game) => (game.eqPos || 0) > 0
);
export const getShowRedo = createSelector(
  [getGame],
  (game) => (game.eqPos || 0) < (game.eq || []).length
);
export const getGridWidth = createSelector([getApp], (app) => {
  if (isForceMobile()) {
    // Use mobile width for app preview video
    return 430 - 22;
  }
  return app.screenWidth > config.desktop ? 800 : app.screenWidth - 22;
});

const getHeightDiff = (w: number) => {
  if (w >= 1920) return 305;
  else if (w >= 1280) return 250;
  else if (w >= 960) return 275;
  else if (w >= 600) return 250;
  return 235;
};

export const getGridHeight = createSelector([getApp], (app) => {
  if (isForceMobile()) {
    // Use mobile height for app preview video
    return (
      932 -
      getHeightDiff(430) -
      (app.equationDiv ? app.equationDiv.clientHeight : 0)
    );
  }
  return (
    app.screenHeight -
    getHeightDiff(app.screenWidth) -
    (app.equationDiv ? app.equationDiv.clientHeight : 0)
  );
});

export const getViewportScale = createSelector([getApp], (app) => {
  if (isForceMobile()) {
    return 3.0;
  }
  // Natural scaling based on the smallest viewport dimension
  const minDimension = Math.min(app.screenWidth, app.screenHeight);
  const baseDimension = 768; // Base tablet size = 1.0 scale
  const minScale = 1.0;
  const maxScale = 1.6; // Reduced from 2.0 to 1.6 for more conservative scaling
  if (minDimension <= baseDimension) {
    return minScale;
  }
  // Calculate scale: linear interpolation from baseDimension to max dimension
  const maxDimension = 1200; // Increased from 1080 to 1200 for gentler curve
  const scaleRatio =
    (minDimension - baseDimension) / (maxDimension - baseDimension);
  const scale = minScale + scaleRatio * (maxScale - minScale);
  // Clamp between min and max scale
  return Math.min(Math.max(scale, minScale), maxScale);
});
export const getTileWidth = createSelector(
  [getGridWidth, getGridHeight, getGame, getViewportScale],
  (gridWidth, gridHeight, game, viewportScale) => {
    if (isForceMobile()) {
      // Use mobile tile size for app preview video
      return Math.min(
        Math.max(
          40,
          Math.min(
            Math.floor((932 - getHeightDiff(430)) / ((game.rows || 3) + 1)) - 4,
            Math.floor((430 - 22) / (game.cols || 3)) - config.padding
          )
        ),
        100 / 3.0
      );
    }
    return Math.min(
      Math.max(
        40,
        Math.min(
          Math.floor(gridHeight / ((game.rows || 3) + 1)) - 4,
          Math.floor(gridWidth / (game.cols || 3)) - config.padding
        )
      ),
      100 / viewportScale
    );
  }
);
export const getOpWidth = createSelector(
  [getTileWidth, getGridWidth],
  (tileWidth, gridWidth) => {
    if (isForceMobile()) {
      // Use mobile op width for app preview video
      return Math.min(
        tileWidth,
        Math.floor((430 - 22) / config.operators.length) - config.padding
      );
    }
    return Math.min(
      tileWidth,
      Math.floor(gridWidth / config.operators.length) - config.padding
    );
  }
);

export const getAchievements = createSelector(
  [getGame, getSolutions],
  (game, solutions) =>
    config.achievements.map((achievement) => ({
      ...achievement,
      achieved: hasAchieved(
        achievement,
        solutions || [],
        game.target || 100,
        game.grid || []
      ),
    })) as AchievementType[]
);
export const getAchievementCount = createSelector(
  [getAchievements],
  (achievements) => achievements.filter((a) => a.achieved).length
);
export const getMessage = createSelector(
  [getGame, getAchievementCount, getIsComplete],
  (game, achievementCount, isComplete) =>
    (isComplete
      ? {
          solved: true,
          achievements: game.achievementDiff,
          solutionNew: game.solutionNew,
        }
      : false) as MessageType
);
export const getHighlightAchievements = createSelector(
  [getGame],
  (game) => (game.achievementDiff || []).length > 0
);
export const getShowTitleScreen = createSelector(
  [getApp, getGame],
  (app, game) => app.titleScreen || (game.grid || []).length === 0
);
export let grids: string[] = [];
// document.addEventListener("deviceready", () => {
// console.log("grids ready", grids);
// });

const getGrids = () => {
  // console.log("getOldGridList type", typeof getOldGridList);
  //  || getOldGridList()
  const newGrids: string[] = ls.get("grids") || [];
  // console.log("newGrids", newGrids);
  if (newGrids.join(";;") !== grids.join(";;")) {
    grids = newGrids;
  }
  return grids;
};
// ls.on("grids", (newGrids) => {
//   console.log("ON GRIDS", newGrids);
//   grids = newGrids;
// });
export const getHistoryGrids = createSelector(
  [getApp, getGrids],
  (app, g) => g
  // getOldGridList().filter((a) => ls(a).solutions)
);

// console.log(localStorage);

export const getHistoryAchievements = createSelector(
  [getGame, getAchievements, getSolutions, getHistoryGrids],
  (game, achievements, solutions, grids) =>
    [
      ...grids
        .filter((d) => d !== `${game.target},${(game.grid || []).join(",")}`)
        .map((data) => ({
          target: parseInt(data.split(",")[0], 10),
          grid: data
            .split(",")
            .slice(1)
            .map((a) => parseInt(a, 10)),
          solutions: ls.get<GameStateType>(data).solutions,
          achievements: config.achievements.map((achievement) => ({
            ...achievement,
            achieved: hasAchieved(
              achievement,
              ls.get<GameStateType>(data).solutions || [],
              parseInt(data.split(",")[0], 10),
              data
                .split(",")
                .slice(1)
                .map((a) => parseInt(a, 10))
            ),
          })),
        }))
        .filter(
          (d) =>
            !(
              game &&
              d.target === game.target &&
              d.grid
                .slice(0)
                .sort((a, b) => a - b)
                .join(",") ===
                (game.grid || [])
                  .slice(0)
                  .sort((a, b) => a - b)
                  .join(",")
            )
        ),
      {
        target: game.target,
        grid: game.grid,
        solutions,
        achievements,
      },
    ] as HistoryType[]
);
